import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { endpoints } from '../api/endpoints';
import type { AssessResult } from '../api/types';
import { ErrorBox, formatScore, StatCard } from '../components/common';

/** Языки, для которых analyzer умеет строить IPA и проверять грамматику. */
export const ASSESS_LANGUAGES = ['en', 'ru', 'es', 'zh', 'de', 'fr', 'it', 'pt', 'pl', 'uk', 'tr', 'nl', 'cs'] as const;

function ScoreBar({ label, value }: { label: string; value: number }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className="score-bar">
      <StatCard label={label} value={formatScore(v)} />
      <meter min={0} max={100} low={60} high={85} optimum={100} value={v} aria-label={label}>
        {formatScore(v)}
      </meter>
    </div>
  );
}

export function AssessmentPage() {
  const { t, i18n } = useTranslation();
  const [expected, setExpected] = useState('');
  const [language, setLanguage] = useState<string>(() => {
    const ui = i18n.language?.split('-')[0];
    return (ASSESS_LANGUAGES as readonly string[]).includes(ui) ? ui : 'en';
  });
  const [audio, setAudio] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<AssessResult | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const resultHeading = useRef<HTMLHeadingElement>(null);
  const canRecord = typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined' && !!navigator.mediaDevices;

  useEffect(() => {
    if (!audio) {
      setAudioUrl(null);
      return;
    }
    if (typeof URL.createObjectURL !== 'function') return;
    const url = URL.createObjectURL(audio);
    setAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [audio]);

  useEffect(
    () => () => {
      if (recorder.current?.state === 'recording') recorder.current.stop();
    },
    [],
  );

  useEffect(() => {
    if (result) resultHeading.current?.focus();
  }, [result]);

  const toggleRecording = async () => {
    if (recording) {
      recorder.current?.stop();
      setRecording(false);
      return;
    }
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunks.push(e.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setAudio(new Blob(chunks, { type: rec.mimeType || 'audio/webm' }));
      };
      recorder.current = rec;
      rec.start();
      setResult(null);
      setRecording(true);
    } catch {
      setError(new Error(t('live.micDenied')));
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!audio) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await endpoints.assess.utterance(audio, expected.trim(), language));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1>{t('assess.title')}</h1>
      <p className="muted">{t('assess.intro')}</p>
      <form className="card form" onSubmit={(e) => void submit(e)} aria-busy={busy}>
        <label>
          {t('assess.expectedText')}
          <textarea rows={3} value={expected} onChange={(e) => setExpected(e.target.value)} maxLength={2000} />
        </label>
        <label>
          {t('assess.language')}
          <select value={language} onChange={(e) => setLanguage(e.target.value)}>
            {ASSESS_LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {t(`assess.languages.${l}`)}
              </option>
            ))}
          </select>
        </label>
        <fieldset>
          <legend>{t('assess.audio')}</legend>
          <div className="actions">
            {canRecord && (
              <button type="button" className={recording ? 'danger' : 'secondary'} aria-pressed={recording} onClick={() => void toggleRecording()}>
                {recording ? t('assess.stopRecording') : t('assess.record')}
              </button>
            )}
            <label>
              {t('assess.uploadFile')}
              <input
                type="file"
                accept="audio/*,.wav,.mp3,.ogg,.webm,.m4a,.flac"
                disabled={recording}
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  setResult(null);
                  setAudio(file);
                }}
              />
            </label>
          </div>
          <p role="status" aria-live="polite" className="muted">
            {recording ? t('assess.recording') : audio ? t('assess.audioReady') : t('assess.noAudio')}
          </p>
          {audioUrl && <audio controls src={audioUrl} aria-label={t('assess.preview')} />}
        </fieldset>
        <div className="actions">
          <button type="submit" disabled={!audio || recording || busy}>
            {busy ? t('assess.analyzing') : t('assess.submit')}
          </button>
        </div>
      </form>
      <ErrorBox error={error} />
      {result && (
        <section className="card" aria-labelledby="assess-result">
          <h2 id="assess-result" ref={resultHeading} tabIndex={-1}>
            {t('assess.result')}
          </h2>
          <dl className="details-grid">
            <dt>{t('assess.recognized')}</dt>
            <dd lang={language}>{result.recognized_text || '—'}</dd>
          </dl>
          <div className="stats-row">
            <ScoreBar label={t('sessions.accuracy')} value={result.accuracy_score} />
            <ScoreBar label={t('sessions.fluency')} value={result.fluency_score} />
          </div>
          {result.feedback && (
            <p className="alert alert-ok">
              <strong>{t('assess.feedback')}:</strong> {result.feedback}
            </p>
          )}
          <h3>{t('assess.mispronounced')}</h3>
          {result.mispronounced_words.length ? (
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('assess.word')}</th>
                  <th scope="col">{t('assess.expectedIpa')}</th>
                  <th scope="col">{t('assess.receivedIpa')}</th>
                </tr>
              </thead>
              <tbody>
                {result.mispronounced_words.map((w, i) => (
                  <tr key={i}>
                    <td lang={language}>{w.word}</td>
                    <td className="ipa">{w.expected ? `/${w.expected}/` : '—'}</td>
                    <td className="ipa">{w.received ? `/${w.received}/` : t('assess.skipped')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="muted">{t('assess.noMispronounced')}</p>
          )}
          <h3>{t('assess.grammar')}</h3>
          {result.grammar_errors.length ? (
            <ul>
              {result.grammar_errors.map((g, i) => (
                <li key={i}>
                  <q lang={language}>{g.text}</q> — {g.message}
                  {g.suggestion && (
                    <>
                      {' '}
                      ({t('assess.suggestion')}: <q lang={language}>{g.suggestion}</q>)
                    </>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">{t('assess.noGrammar')}</p>
          )}
        </section>
      )}
    </div>
  );
}
